import dynamic from "next/dynamic";
import { useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useLazyQuery } from "@apollo/client/react/hooks";
import Button from "components/shared/button/Button";
import IconClose from "components/shared/svg/IconClose";
import Modal from "react-modal";
import CardNumberInput from "components/shared/cardNumberInput/CardNumberInput";
import ErrorInvalid from "./errorInvalid/ErrorInvalid";
import Balance from "./balance/Balance";

import { GetCardInfo } from "../../../helpers/api/cadeauConcepten/queries/saldoCheck.gql";

import {
    SaldocheckFormStyled,
    HeadingWrapper,
    Image,
    Label,
    Content,
    Close,
    SubmitButton
} from "./BalanceCheckForm.styled";

const Expired = dynamic(() => import("./expired/Expired"));

export default function BalanceCheckForm() {
    const [buttonIsDisabled, setButtonIsDisabled] = useState(true);
    const [modalIsOpen, setIsOpen] = useState(false);
    const [isValid, setIsValid] = useState(true);
    const [hasBalance, setHasBalance] = useState(false);
    const [isExpired, setIsExpired] = useState(false);

    const methods = useForm({
        mode: "onBlur",
        defaultValues: {
            code: ["6064"]
        }
    });
    const { handleSubmit, watch } = methods;

    const giftcardNumber = watch("code")?.join("");

    const [getCardInfo, { data, error, loading }] = useLazyQuery(GetCardInfo);

    useEffect(() => {
        if (giftcardNumber && giftcardNumber.length === 19) {
            if (!Number.isNaN(giftcardNumber) && isValid) {
                setButtonIsDisabled(false);
            } else {
                setButtonIsDisabled(true);
            }
        } else {
            setButtonIsDisabled(true);
            setIsValid(true);
            setHasBalance(false);
            setIsExpired(false);
        }
    }, [giftcardNumber, isValid]);

    function openModal() {
        setIsOpen(true);
    }

    function closeModal() {
        setIsOpen(false);
    }

    function onSubmit() {
        if (giftcardNumber.length === 19) {
            getCardInfo({
                variables: {
                    giftcardNumber
                }
            });
        } else {
            setIsValid(false);
        }
    }

    useEffect(() => {
        if (error) setIsValid(false);

        if (data?.getCardInfo) {
            if (data.getCardInfo.giftcardNumber.toString().length !== 19) {
                setIsValid(false);
            } else if (data.getCardInfo.expired === false) {
                setIsValid(true);
                setHasBalance(true);
            } else if (data.getCardInfo.expired === true) {
                setIsValid(true);
                setIsExpired(true);
            }
        } else {
            setIsValid(false);
        }
    }, [data, error]);

    return (
        <SaldocheckFormStyled>
            <HeadingWrapper>
                <Label>Vul het 19-cijferig kaartnummer in</Label>
                <Button type="button" variant="ghost" onClick={openModal}>
                    Waar vind ik mijn kaartnummer?
                </Button>
                <Modal
                    isOpen={modalIsOpen}
                    contentLabel="Waar vind ik mijn kaartnummer?"
                    onRequestClose={closeModal}
                    ariaHideApp={false}
                    className="react-modal"
                    overlayClassName="react-modal-overlay"
                    closeTimeoutMS={400}
                >
                    <Content>
                        <Image
                            src="/images/giftcard-backside.svg"
                            height={580}
                            width={980}
                            alt="Cadeaukaart achterkant"
                            loading="lazy"
                        />
                        <Close onClick={closeModal}>
                            <IconClose />
                        </Close>
                    </Content>
                </Modal>
            </HeadingWrapper>

            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <CardNumberInput
                        hasError={!isValid || isExpired}
                        hasBalance={hasBalance}
                    />
                    {!isValid && <ErrorInvalid />}
                    {!hasBalance && !isExpired && (
                        <SubmitButton>
                            <Button
                                type="submit"
                                disabled={buttonIsDisabled || loading}
                            >
                                check je saldo
                            </Button>
                        </SubmitButton>
                    )}
                </form>
                {isExpired && !hasBalance && (
                    <Expired
                        cardNumber={data.getCardInfo.giftcardNumber}
                        date={data.getCardInfo.expirationDate}
                    />
                )}
                {hasBalance && isValid && (
                    <Balance
                        value={data.getCardInfo.value}
                        date={data.getCardInfo.expirationDate}
                    />
                )}
            </FormProvider>
        </SaldocheckFormStyled>
    );
}
